export const UCC_STATUS = {
  INCOMPLETE: "Incomplete",
  PENDING_USER_VERIFICATION: "Pending — User Verification",
  PENDING_PROPERTY_VERIFICATION: "Pending — Property Verification",
  PENDING_NO_SIGNED_CONTRACT: "Pending — No Signed Contract",
  PENDING_SKIPPED_SECURITY_QUESTIONS: "Pending — Skipped Security Questions",
  READY_TO_GENERATE: "Ready to Generate",
  NOT_APPLICABLE: "Not Applicable",
  SENT_TO_RECORD: "Sent to Record",
  REJECTED: "Rejected",
  SENT_TO_RERECORD: "Sent to Re-Record",
  RECORDED: "Recorded",
  LEGACY_PENDING: "Pending",
  LEGACY_ON_HOLD: "On Hold",
  LEGACY_RELEASED: "Released for Rerecording",
  LEGACY_GENERATED: "Generated",
  LEGACY_SENT_TO_RERECORD: "Sent to Rerecord",
  LEGACY_MULTI_PROPERTY: "Multi-Property",
};

/** @deprecated Use SENT_TO_RECORD */
export const UCC_STATUS_GENERATED_ALIAS = UCC_STATUS.SENT_TO_RECORD;

// Backward-compatible aliases used across admin UI.
UCC_STATUS.GENERATED = UCC_STATUS.SENT_TO_RECORD;
UCC_STATUS.MULTI_PROPERTY = UCC_STATUS.LEGACY_MULTI_PROPERTY;

export const UCC_STATUS_SLUGS = {
  [UCC_STATUS.INCOMPLETE]: "incomplete",
  [UCC_STATUS.PENDING_USER_VERIFICATION]: "pending_user_verification",
  [UCC_STATUS.PENDING_PROPERTY_VERIFICATION]: "pending_property_verification",
  [UCC_STATUS.PENDING_NO_SIGNED_CONTRACT]: "pending_no_signed_contract",
  [UCC_STATUS.PENDING_SKIPPED_SECURITY_QUESTIONS]:
    "pending_skipped_security_questions",
  [UCC_STATUS.READY_TO_GENERATE]: "ready_to_generate",
  [UCC_STATUS.NOT_APPLICABLE]: "not_applicable",
  [UCC_STATUS.SENT_TO_RECORD]: "sent_to_record",
  [UCC_STATUS.REJECTED]: "rejected",
  [UCC_STATUS.SENT_TO_RERECORD]: "sent_to_rerecord",
  [UCC_STATUS.RECORDED]: "recorded",
  [UCC_STATUS.LEGACY_PENDING]: "pending",
  [UCC_STATUS.LEGACY_ON_HOLD]: "on_hold",
  [UCC_STATUS.LEGACY_RELEASED]: "rerecord",
  [UCC_STATUS.LEGACY_GENERATED]: "generated",
  [UCC_STATUS.LEGACY_MULTI_PROPERTY]: "multi_property",
};

export const UCC_STATUS_OPTIONS = [
  {
    value: UCC_STATUS.INCOMPLETE,
    slug: "incomplete",
    manual: false,
  },
  {
    value: UCC_STATUS.PENDING_PROPERTY_VERIFICATION,
    slug: "pending_property_verification",
    manual: false,
  },
  {
    value: UCC_STATUS.READY_TO_GENERATE,
    slug: "ready_to_generate",
    manual: false,
  },
  { value: UCC_STATUS.SENT_TO_RECORD, slug: "sent_to_record", manual: false },
  { value: UCC_STATUS.REJECTED, slug: "rejected", manual: false },
  {
    value: UCC_STATUS.SENT_TO_RERECORD,
    slug: "sent_to_rerecord",
    manual: true,
  },
  { value: UCC_STATUS.RECORDED, slug: "recorded", manual: false },
];

export const UCC_LEGACY_FILTER_OPTIONS = [
  { label: "On Hold (legacy)", value: "on_hold" },
  { label: "Pending (legacy)", value: "pending" },
  { label: "Released for Rerecording (legacy)", value: "rerecord" },
  { label: "Generated (legacy)", value: "generated" },
];

export const UCC_MANUAL_ONLY_STATUSES = UCC_STATUS_OPTIONS.filter(
  (option) => option.manual,
).map((option) => option.value);

export const UCC_AVANZE_QUEUE_STATUSES = [
  UCC_STATUS.READY_TO_GENERATE,
  UCC_STATUS.SENT_TO_RERECORD,
];

export const normalizeUccStatusSlug = (value) => {
  if (!value) {
    return "";
  }
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
};

/** User-facing labels; stored/API values stay in UCC_STATUS. */
export const UCC_STATUS_DISPLAY_LABELS = {
  [UCC_STATUS.PENDING_USER_VERIFICATION]: "Incomplete",
  [UCC_STATUS.PENDING_NO_SIGNED_CONTRACT]: "Incomplete",
  [UCC_STATUS.PENDING_SKIPPED_SECURITY_QUESTIONS]:
    "Pending — Property Verification",
  [UCC_STATUS.LEGACY_GENERATED]: "Sent to Record",
  [UCC_STATUS.LEGACY_SENT_TO_RERECORD]: "Sent to Re-Record",
  [UCC_STATUS.LEGACY_MULTI_PROPERTY]: "Ready to Generate",
  [UCC_STATUS.NOT_APPLICABLE]: "Not Applicable",
};

export const UCC_STATUS_SHORT_LABELS = {
  [UCC_STATUS.INCOMPLETE]: "Incomplete",
  [UCC_STATUS.PENDING_USER_VERIFICATION]: "Incomplete",
  [UCC_STATUS.PENDING_PROPERTY_VERIFICATION]: "Pending Property Verification",
  [UCC_STATUS.PENDING_NO_SIGNED_CONTRACT]: "No contract",
  [UCC_STATUS.PENDING_SKIPPED_SECURITY_QUESTIONS]: "Skipped questions",
  [UCC_STATUS.READY_TO_GENERATE]: "Ready",
  [UCC_STATUS.NOT_APPLICABLE]: "N/A",
  [UCC_STATUS.SENT_TO_RECORD]: "Sent to Record",
  [UCC_STATUS.REJECTED]: "Rejected",
  [UCC_STATUS.SENT_TO_RERECORD]: "Re-Record",
  [UCC_STATUS.RECORDED]: "Recorded",
  [UCC_STATUS.LEGACY_PENDING]: "Pending",
  [UCC_STATUS.LEGACY_ON_HOLD]: "On hold",
  [UCC_STATUS.LEGACY_RELEASED]: "Re-Record",
  [UCC_STATUS.LEGACY_GENERATED]: "Sent to Record",
  [UCC_STATUS.LEGACY_SENT_TO_RERECORD]: "Re-Record",
  [UCC_STATUS.LEGACY_MULTI_PROPERTY]: "Multi-property",
};

export const resolveUccStatusLabel = (status) => {
  if (!status) {
    return UCC_STATUS.READY_TO_GENERATE;
  }

  const normalized = String(status).trim();
  const slug = normalizeUccStatusSlug(normalized);

  if (slug === "generated" || normalized === UCC_STATUS.LEGACY_GENERATED) {
    return UCC_STATUS.SENT_TO_RECORD;
  }

  if (
    slug === "not_applicable" ||
    normalized.toLowerCase() === "not applicable"
  ) {
    return UCC_STATUS.NOT_APPLICABLE;
  }

  if (
    slug === "sent_to_rerecord" ||
    slug === "sent_to_re_record" ||
    normalized === UCC_STATUS.LEGACY_SENT_TO_RERECORD
  ) {
    return UCC_STATUS.SENT_TO_RERECORD;
  }

  if (
    slug === "multi_property" ||
    normalized === UCC_STATUS.LEGACY_MULTI_PROPERTY
  ) {
    return UCC_STATUS.READY_TO_GENERATE;
  }

  const match = UCC_STATUS_OPTIONS.find(
    (option) =>
      option.value === normalized ||
      option.slug === slug ||
      UCC_STATUS_DISPLAY_LABELS[option.value] === normalized,
  );

  if (match) {
    return match.value;
  }

  if (normalized === UCC_STATUS.LEGACY_RELEASED) {
    return UCC_STATUS.SENT_TO_RERECORD;
  }

  if (normalized === UCC_STATUS.LEGACY_ON_HOLD) {
    return UCC_STATUS.PENDING_USER_VERIFICATION;
  }

  if (normalized === UCC_STATUS.LEGACY_PENDING) {
    return UCC_STATUS.READY_TO_GENERATE;
  }

  return normalized;
};

export const formatUccStatusDisplayLabel = (status) => {
  const canonical = resolveUccStatusLabel(status);
  return UCC_STATUS_DISPLAY_LABELS[canonical] ?? canonical;
};

export const UCC_FILTER_OPTIONS = [
  ...UCC_STATUS_OPTIONS.map(({ value, slug }) => ({
    label: formatUccStatusDisplayLabel(value),
    value: slug,
  })),
  { label: "Not Applicable", value: "not_applicable" },
  { label: "Multi-property (flag)", value: "multi_property" },
];

export const UCC_FILTER_OPTIONS_WITH_LEGACY = [
  ...UCC_FILTER_OPTIONS,
  ...UCC_LEGACY_FILTER_OPTIONS,
];

/** Aggregated dashboard "pending blockers" cohort for property-list filtering. */
export const UCC_PENDING_BLOCKERS_FILTER = [
  "incomplete",
  "pending_property_verification",
].join(",");

export const resolveUccStatusShortLabel = (status) => {
  const label = resolveUccStatusLabel(status);
  return UCC_STATUS_SHORT_LABELS[label] ?? formatUccStatusDisplayLabel(label);
};

/** Statuses that need admin action — drives the Manage UCCs attention badge. */
export const UCC_ATTENTION_STATUSES = [
  // UCC_STATUS.INCOMPLETE,
  UCC_STATUS.PENDING_PROPERTY_VERIFICATION,
  UCC_STATUS.READY_TO_GENERATE,
  UCC_STATUS.REJECTED,
];

export const uccNeedsAttention = (status) =>
  UCC_ATTENTION_STATUSES.includes(resolveUccStatusLabel(status));

/** Rows listed in Manage UCCs: Complete Protection with a real subscription. */
export const isManageUccRow = (property) => {
  const subscriptionStatus = String(
    property?.subscription_status || "",
  ).toLowerCase();
  return (
    (property?.product_tier === "complete_protection" ||
      !property?.product_tier) &&
    subscriptionStatus !== "not applicable" &&
    subscriptionStatus !== "none"
  );
};

// Counts only rows Manage UCCs lists, so the badge never shows a row the list hides.
export const countUccAttention = (properties = []) =>
  properties.filter((property) => {
    const subscriptionStatus = String(
      property?.subscription_status || "",
    ).toLowerCase();
    return (
      isManageUccRow(property) &&
      subscriptionStatus !== "cancelled" &&
      subscriptionStatus !== "canceled" &&
      subscriptionStatus !== "declined" &&
      !property?.terminated &&
      uccNeedsAttention(property?.ucc_status)
    );
  }).length;

export const uccStatusChipColor = (status) => {
  const label = resolveUccStatusLabel(status);
  const normalized = label.toLowerCase();

  if (
    label === UCC_STATUS.SENT_TO_RECORD ||
    label === UCC_STATUS.RECORDED ||
    normalized.includes("recorded") ||
    normalized.includes("sent to record")
  ) {
    return "success";
  }
  if (normalized.includes("rejected")) {
    return "error";
  }
  if (normalized.includes("multi-property")) {
    return "secondary";
  }
  if (
    normalized.includes("ready to generate") ||
    normalized.includes("re-record") ||
    normalized.includes("rerecord")
  ) {
    return "info";
  }
  if (normalized.includes("not applicable")) {
    return "default";
  }
  return "warning";
};

export const allowsUccGeneration = (status) => {
  const label = resolveUccStatusLabel(status);
  return UCC_AVANZE_QUEUE_STATUSES.includes(label);
};

export const blocksUccAutoGeneration = (status) => !allowsUccGeneration(status);
